import { type CSSProperties, type ReactNode, type RefObject } from "react";
import { Virtualizer, type VirtualizerHandle } from "virtua";
import { hiddenUntilScrollClass, SCROLL_SURFACE_CLASS } from "@tellescope/react-hooks";
import { cn } from "../../lib/utils";
import { EndAnchoredContent } from "./EndAnchoredContent";
import { MinWidthContent } from "./MinWidthContent";
import type { PaginatedListScrollbar } from "./types";

export interface PaginatedListViewportProps {
  children: ReactNode;
  className?: string;
  label: string;
  virtualizerRef: RefObject<VirtualizerHandle | null>;
  scrollerRef: (element: HTMLDivElement | null) => void;
  onScroll: (offset: number) => void;
  /** The scroller `virtua` should measure and listen against, when an ancestor owns scrolling. */
  externalScrollRef: RefObject<HTMLElement | null>;
  startMargin: number | undefined;
  /** False when an ancestor owns scrolling, which leaves this element non-scrolling. */
  ownsScrolling: boolean;
  scrollbar: PaginatedListScrollbar;
  /** Whether a scroll is in progress, for the `hidden-until-scroll` thumb. */
  isScrolling: boolean;
  style: CSSProperties | undefined;
  /** See `PaginatedListProps.resetKey`. Applied as the `Virtualizer`'s `key` to force a remount. */
  resetKey: string | number | undefined;
  /** Whether this pass prepended children, in which case `virtua` holds the viewport steady. */
  shift: boolean;
  onFocusCapture: () => void;
  onClickCapture: () => void;
  /**
   * Adds the bottom-anchoring structure: a flex spacer, opted-out browser scroll anchoring, and the
   * content element that opens at and follows the end.
   */
  isAnchoredToEnd: boolean;
  /** See `PaginatedListProps.contentMinWidth`. */
  contentMinWidth: number | undefined;
  /** See `PaginatedListProps.role`. */
  role: string | undefined;
}

export const PaginatedListViewport = ({
  children,
  className,
  label,
  virtualizerRef,
  scrollerRef,
  onScroll,
  externalScrollRef,
  startMargin,
  ownsScrolling,
  scrollbar,
  isScrolling,
  style,
  resetKey,
  shift,
  onFocusCapture,
  onClickCapture,
  isAnchoredToEnd,
  contentMinWidth,
  role,
}: PaginatedListViewportProps) => (
  <div
    ref={scrollerRef}
    data-slot="paginated-list-viewport"
    onFocusCapture={onFocusCapture}
    onClickCapture={onClickCapture}
    // Rows are typically non-focusable, which leaves a scroll container unreachable by keyboard.
    // Focusable only while this element is the scroller — otherwise the tab stop scrolls nothing.
    {...(ownsScrolling ? { tabIndex: 0, role: role ?? "region", "aria-label": label } : {})}
    className={cn(
      ownsScrolling && "overflow-y-auto",
      ownsScrolling && scrollbar === "hidden-until-scroll" && hiddenUntilScrollClass(isScrolling),
      ownsScrolling && scrollbar === "floating" && SCROLL_SURFACE_CLASS,
      // The browser's own scroll anchoring competes with the virtualizer's, so virtua's chat pattern
      // turns it off on the scroller. Flex column is what lets the spacer below do its work.
      isAnchoredToEnd && "flex flex-col [overflow-anchor:none]",
      className
    )}
    style={
      contentMinWidth === undefined ? style : { ...style, "--paginated-list-content-min-width": `${contentMinWidth}px` }
    }
  >
    {/* Holds the messages against the bottom of the viewport while there are too few to fill it,
        which is what makes a short thread read like a chat rather than a list. Once the content
        overflows, the spacer collapses and the virtualizer scrolls normally. */}
    {isAnchoredToEnd && <div className="grow" />}
    <Virtualizer
      key={resetKey}
      ref={virtualizerRef}
      onScroll={onScroll}
      scrollRef={ownsScrolling ? undefined : externalScrollRef}
      startMargin={startMargin}
      shift={shift}
      as={isAnchoredToEnd ? EndAnchoredContent : contentMinWidth !== undefined ? MinWidthContent : undefined}
    >
      {children}
    </Virtualizer>
  </div>
);
